import React from 'react';
import { Box, Text } from '@mantine/core';
import { IconTrendingDown, IconTrendingUp } from '@tabler/icons-react';

import ProductSalesLineGraph from '../../graphs/ProductSalesLineGraph';
import type { Request } from '../../../codegen/graphql';
import {
    getPriceChangeScope,
    getSuggestedStoreIds,
} from '../../../helpers/priceChangeScope';
import { getSalesTrendForView } from '../../../helpers/productSalesTrend';
import { useProductSalesScope } from '../../../hooks/useProductSalesScope';
import { useOptionalBatchSalesScope } from '../batches/BatchSalesScopeContext';
import {
    PRODUCT_SALES_CELL_DETAIL_DROPDOWN_CLASSNAME,
    THIN_BORDER_CELL_DETAIL_TRIGGER_CLASSNAME,
} from './cellDetailSurfaceClasses';
import { CellDetailTriggerChevron } from './cellDetailTriggerChevron';
import { TableCellDetailPopover } from './TableCellDetailPopover';
import { When } from '../../common';

type Props = {
    request: Request;
};

export function ProductSalesTableCell({ request }: Props) {
    const localSalesScope = useProductSalesScope({
        advertisingZoneId: request.advertisingZoneId,
        priceChangeScope: getPriceChangeScope(request),
        suggestedStoreIds: getSuggestedStoreIds(request),
    });
    const batchSalesScope = useOptionalBatchSalesScope();
    const salesScope = batchSalesScope ?? localSalesScope;
    const trend = getSalesTrendForView(request, salesScope.scope);
    const showNeutral = trend !== 'increase' && trend !== 'decrease';
    const isIncrease = trend === 'increase';
    const popoverTitle = 'Product Sales';

    return (
        <When
            condition={showNeutral}
            fallback={
                <div className="flex w-full justify-start">
                    <TableCellDetailPopover
                        title={popoverTitle}
                        triggerClassName={
                            THIN_BORDER_CELL_DETAIL_TRIGGER_CLASSNAME
                        }
                        dropdownClassName={
                            PRODUCT_SALES_CELL_DETAIL_DROPDOWN_CLASSNAME
                        }
                        constrainToAvailableSpace
                        position="top"
                        trigger={
                            <div className="flex h-full min-h-0 w-full items-center justify-between gap-2">
                                <div className="flex shrink-0 items-center gap-2">
                                    <When condition={isIncrease}>
                                        <IconTrendingUp
                                            size={18}
                                            className="shrink-0 text-emerald-600"
                                            stroke={1.5}
                                        />
                                    </When>
                                    <When condition={!isIncrease}>
                                        <IconTrendingDown
                                            size={18}
                                            className="shrink-0 text-red-600"
                                            stroke={1.5}
                                        />
                                    </When>
                                    <Text
                                        size="sm"
                                        fw={600}
                                        className={`whitespace-nowrap ${
                                            isIncrease
                                                ? 'text-emerald-700'
                                                : 'text-red-700'
                                        }`}
                                    >
                                        <When
                                            condition={isIncrease}
                                            fallback="Decrease"
                                        >
                                            Increase
                                        </When>
                                    </Text>
                                </div>
                                <CellDetailTriggerChevron />
                            </div>
                        }
                    >
                        <Box className="min-w-0" p="md">
                            <ProductSalesLineGraph
                                request={request}
                                salesScope={salesScope}
                                layout="compact"
                            />
                        </Box>
                    </TableCellDetailPopover>
                </div>
            }
        >
            <Text size="sm" c="dimmed">
                —
            </Text>
        </When>
    );
}
